import { action, flow, makeObservable, observable } from 'mobx';
import { GroupAdminApi } from '../apis';
import { CERTIFICATE_EXPORT_JOB_POLL_INTERVAL_MS, STUDENT_STATUS } from '../core/configs/constants';
import BaseStore from './BaseStore';

class GroupAdminStore extends BaseStore {

    studentsOfCompany = [];
    studentsOfCompanyActive = [];
    studentsOfCompanyOtherActive = [];
    disabledStudentIdx = [];
    pagingStudentOfCompany = this.defaultPaging;

    studentDetail = {};

    courseListByStudent = {};
    studentListByAttendanceCode = {};

    attendancesOfCompany = [];

    ticketByGroup = [];

    companyAttendanceRateList = [];
    studentListWithAttCompany = [];

    // #17989 - per-table loading flags for the GA dashboard. These two calls bypass the
    // full-screen loading overlay (ApiStore.call disableLoading) so the page renders right after
    // login and each table shows its own skeleton while its figures are still loading.
    loadingAttendancesOfCompany = false;
    loadingCompanyAttendanceRate = false;
    // Not observable: sequence numbers so a slow earlier response can never overwrite the latest
    // page/filter the GA asked for (the overlay used to serialize these calls by blocking clicks).
    attendancesOfCompanySeq = 0;
    companyAttendanceRateSeq = 0;

    childrenOfGroup = [];

    expandTreeChildrenGroupIds = [];
    selectedTreeChildrenGroupIds = [];
    selectedGroupIds = [];

    /** GA header filter: company (`group`) vs department (`department`). */
    gaFilterMode = 'group';
    childrenDepartments = [];
    expandTreeDepartmentIds = [];
    selectedTreeDepartmentIds = [];
    selectedDepartmentIds = [];
    masterSelectedGroupIds = [];

    // test history ei
    testHistoriesByStudent = [];
    pagingTestHistoriesByStudent = { ...this.defaultPaging };

    // #17889 follow-up — corner "download manager" queue (Google Drive style) for background
    // certificate export jobs. Lives here on the store, not on CertificateDownloadScreen's local
    // state, specifically so progress survives the GA navigating to another screen — the job
    // keeps running server-side regardless, this just keeps the UI honest about it. Resets on a
    // page reload (in-memory only), same as a browser's own download tray — not meant to survive
    // a refresh, and there's currently no persistence layer for that.
    // Each entry: { jobId, label, status, processedCount, totalCount, downloadUrl, error }
    exportJobs = [];
    // Not observable — interval ids are an implementation detail, not UI state.
    exportJobTimers = {};

    constructor(rootStore) {
        super();
        makeObservable(this, {
            studentsOfCompany: observable,
            studentsOfCompanyActive: observable,
            studentsOfCompanyOtherActive: observable,
            disabledStudentIdx: observable,
            pagingStudentOfCompany: observable,
            studentDetail: observable,
            attendancesOfCompany: observable,
            courseListByStudent: observable,
            studentListByAttendanceCode: observable,
            ticketByGroup: observable,
            companyAttendanceRateList: observable,
            studentListWithAttCompany: observable,
            loadingAttendancesOfCompany: observable,
            loadingCompanyAttendanceRate: observable,
            childrenOfGroup: observable,
            expandTreeChildrenGroupIds: observable,
            selectedTreeChildrenGroupIds: observable,
            selectedGroupIds: observable,
            gaFilterMode: observable,
            childrenDepartments: observable,
            expandTreeDepartmentIds: observable,
            selectedTreeDepartmentIds: observable,
            selectedDepartmentIds: observable,
            masterSelectedGroupIds: observable,
            getStudentsOfCompanyByCourseStatus: flow.bound,
            getAttendancesOfCompany: flow.bound,
            getStudentsOfCompany: flow.bound,
            getStudentIdsByTicket: flow.bound,
            createCertificateExportJob: flow.bound,
            pollCertificateExportJobStatus: flow.bound,
            getCertificateExportJobDownloadUrl: flow.bound,
            exportJobs: observable,
            startCertificateExportJob: action.bound,
            getStudentDetail: flow.bound,
            getCourseListByStudent: flow.bound,
            getStudentListByAttendanceCode: flow.bound,
            getTicketByGroup: flow.bound,
            searchAttendancesOfCompany: flow.bound,
            getTicketPlansForGA: flow.bound,
            exportStudentCSV: flow.bound,
            updateStatusStudent: flow.bound,
            getApprovalAttendanceCodes: flow.bound,
            approveStudent: flow.bound,
            rejectStudent: flow.bound,
            updateStudentEmployeeCode: flow.bound,
            getCompanyAttendanceRate: flow.bound,
            getDetailCompanyAttendanceRate: flow.bound,
            exportTestResultCsv: flow.bound,
            exportAttendanceStudentListCsv: flow.bound,
            exportAttendanceCompleteRateCsv: flow.bound,
            getAllChildrenGroupById: flow.bound,
            getCompanyDepartments: flow.bound,
            resignation: flow.bound,
            // test history ei
            testHistoriesByStudent: observable,
            pagingTestHistoriesByStudent: observable,
            getTestHistoriesByStudent: flow.bound,
            getCalculateAverageEi: flow.bound
        });
        this.rootStore = rootStore;
        this.api = new GroupAdminApi();
    }

    *getStudentsOfCompanyByCourseStatus(searchParams) {
        try {
            const { size, page, sortDir, sortKey } = this.pagingStudentOfCompany;
            const payload = { size, page, sortDir, sortKey, ...searchParams };
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getStudentsOfCompanyByCourseStatus, payload);
            if (res?.ok) {
                this.studentsOfCompany = res?.data?.[0]?.elements || [];
                this.setAttrObservable('pagingStudentOfCompany', res?.data?.[0]?.paginate, true, false);
            }
        } catch (error) {
            console.log(error);
        }
    }

    // Resolves the GA-scoped student ids for a ticket plan (bulk certificate download) without
    // touching studentsOfCompany/pagingStudentOfCompany, which back the separate student list screen.
    *getStudentIdsByTicket(ticketId, { size = 5000, status } = {}) {
        try {
            const res = yield this.rootStore.apiStore.call(
                this.api, this.api.getStudentsOfCompany, { ticketId, size, page: 1, ...(status != null ? { status } : {}) }
            );
            if (res?.ok) {
                return (res?.data?.elements || []).map(e => e.id);
            }
        } catch (error) {
            console.log(error);
        }
        return [];
    }

    // Bulk certificate export (large selection, see #17889 scale follow-up / Figma redesign):
    // userIds is the GA's checkbox selection from the eligible-students picker — the server
    // re-validates every id against the same eligibility rule before creating the job.
    *createCertificateExportJob(ticketId, courseIds, userIds) {
        try {
            const res = yield this.rootStore.apiStore.call(
                this.api, this.api.createCertificateExportJob, { ticketId, courseIds, userIds }
            );
            return res;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    // Called on a polling interval — intentionally bypasses apiStore.call so a transient
    // failure doesn't flash the global loading spinner or pop an error modal mid-poll; the
    // screen decides how to react (retry / give up) based on the null return.
    *pollCertificateExportJobStatus(jobId) {
        try {
            // Bypassing apiStore.call means `res` here is the raw axios response, so the API
            // envelope is one level deeper than usual (res.data = {ok, data, message}) — unwrap
            // to res.data.data to hand the screen the flat {status, totalCount, processedCount}.
            const res = yield this.api.getCertificateExportJobStatus(jobId);
            return res?.data?.ok ? res.data.data : null;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    *getCertificateExportJobDownloadUrl(jobId) {
        try {
            const res = yield this.api.getCertificateExportJobDownloadUrl(jobId);
            return res?.data;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    // Kicks off a background export job and adds it to the corner widget queue — see exportJobs
    // above for why this lives here instead of on the screen. `label` is just display text (e.g.
    // the ticket plan name), the screen already has it and it's cheaper to pass through than to
    // fetch/derive it again here.
    async startCertificateExportJob(ticketId, courseIds, userIds, label) {
        const res = await this.createCertificateExportJob(ticketId, courseIds, userIds);
        if (!res?.ok) {
            return res;
        }

        const job = {
            jobId: res.data.jobId,
            label,
            status: 'PENDING',
            processedCount: 0,
            totalCount: res.data.totalCount ?? userIds.length,
            downloadUrl: null,
            error: null
        };
        this.setAttrObservable('exportJobs', [...this.exportJobs, job]);
        this.pollExportJob(job.jobId);
        return res;
    }

    updateExportJob(jobId, patch) {
        this.setAttrObservable(
            'exportJobs',
            this.exportJobs.map(j => (j.jobId === jobId ? { ...j, ...patch } : j))
        );
    }

    stopExportJobPolling(jobId) {
        if (this.exportJobTimers[jobId]) {
            clearInterval(this.exportJobTimers[jobId]);
            delete this.exportJobTimers[jobId];
        }
    }

    // Not itself a mobx action (plain recurring callback, not a single call) — every state
    // mutation inside goes through updateExportJob, which is an action via setAttrObservable.
    pollExportJob(jobId) {
        let failureCount = 0;

        this.exportJobTimers[jobId] = setInterval(async () => {
            const status = await this.pollCertificateExportJobStatus(jobId);

            if (!status) {
                failureCount += 1;
                // A handful of transient failures (network blip) is fine — give up only if
                // polling looks genuinely broken, rather than erroring out on the first miss.
                if (failureCount >= 5) {
                    this.stopExportJobPolling(jobId);
                    this.updateExportJob(jobId, {
                        status: 'FAILED',
                        error: '進捗の取得に失敗しました。時間をおいて再度お試しください。'
                    });
                }
                return;
            }
            failureCount = 0;

            if (status.status === 'COMPLETED') {
                this.stopExportJobPolling(jobId);
                const dl = await this.getCertificateExportJobDownloadUrl(jobId);
                if (dl?.ok && dl.data?.url) {
                    this.updateExportJob(jobId, {
                        status: 'COMPLETED',
                        processedCount: status.processedCount,
                        totalCount: status.totalCount,
                        downloadUrl: dl.data.url
                    });
                    // Auto-trigger once — if the GA has since navigated away this is still safe
                    // (just changes what the current tab is displaying next), and the widget's own
                    // "ダウンロード" link (downloadUrl above) is there as a manual fallback either way.
                    window.location.href = dl.data.url;
                } else {
                    this.updateExportJob(jobId, {
                        status: 'FAILED',
                        error: dl?.message || 'ダウンロードURLの取得に失敗しました。'
                    });
                }
            } else if (status.status === 'FAILED') {
                this.stopExportJobPolling(jobId);
                this.updateExportJob(jobId, { status: 'FAILED', error: status.errorMessage || 'エクスポートに失敗しました。' });
            } else {
                this.updateExportJob(jobId, { status: status.status, processedCount: status.processedCount, totalCount: status.totalCount });
            }
        }, CERTIFICATE_EXPORT_JOB_POLL_INTERVAL_MS);
    }

    *getAttendancesOfCompany(searchParams) {
        this.attendancesOfCompanySeq += 1;
        const seq = this.attendancesOfCompanySeq;
        this.loadingAttendancesOfCompany = true;
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getAttendancesOfCompany, searchParams, false, null, true);
            if (seq !== this.attendancesOfCompanySeq) return; // a newer request is in flight
            if (res?.ok) {
                this.attendancesOfCompany = res?.data;
            }
        } catch (error) {
            console.log(error);
        } finally {
            if (seq === this.attendancesOfCompanySeq) this.loadingAttendancesOfCompany = false;
        }
    }

    *getStudentsOfCompany(searchParams) {
        try {
            const { size, page, sortDir, sortKey } = this.pagingStudentOfCompany;
            const payload = { size, page, sortDir, sortKey, ...searchParams };
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getStudentsOfCompany, payload);
            if (res?.ok) {
                this.studentsOfCompany = res?.data?.elements || [];
                this.studentsOfCompanyActive = res?.data?.elements?.filter(e => e.status === STUDENT_STATUS.ACTIVE);
                this.studentsOfCompanyOtherActive = res?.data?.elements?.filter(e => e.status !== STUDENT_STATUS.ACTIVE);
                this.disabledStudentIdx = res?.data?.elements?.map(e => e.status !== STUDENT_STATUS.ACTIVE);
                this.setAttrObservable('pagingStudentOfCompany', res?.data?.paginate, true, false);
            }
        } catch (error) {
            console.log(error);
        }
    }

    *getStudentDetail(id, payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getStudentDetail, { id, payload });
            if (res?.ok) {
                this.studentDetail = res?.data;
            }
        } catch (error) {
            console.log(error);
        }
    }

    *getCourseListByStudent(id, payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getCourseListByStudent, { id, payload });
            if (res?.ok) {
                this.courseListByStudent = res?.data;
            }
        } catch (error) {
            console.log(error);
        }
    }

    *getStudentListByAttendanceCode(id, payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getStudentListByAttendanceCode, { id, payload });
            if (res?.ok) {
                this.studentListByAttendanceCode = res?.data;
            }
        } catch (error) {
            console.log(error);
        }
    }

    *getTicketByGroup(payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getTicketByGroup, payload);
            if (res?.ok) {
                this.ticketByGroup = res?.data;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    // #17899 — 受講コード combobox typeahead. Returns the raw list (not stored as an observable) so the
    // caller can feed it straight into react-select's loadOptions promise, same pattern as
    // CourseStore.getCoursesByName used by the 受講済講座名 search on this same screen.
    *searchAttendancesOfCompany(searchParams) {
        try {
            const payload = { size: 20, page: 1, ...searchParams };
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getAttendancesOfCompany, payload, false, null, true);
            return res?.data?.elements || [];
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    *getTicketPlansForGA(searchParams) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getTicketPlansForGA, searchParams, false, null, true);
            return res?.data || [];
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    *exportStudentCSV(payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.exportStudentCSV, payload);
        } catch (error) {
            console.log(error);
        }
    }

    *updateStatusStudent(id, payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.updateStatusStudent, { id, payload });
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    // Attendance codes the GA may pick on the approval modal; null when the request failed.
    // silent: do not pop the error modal (used to refresh the list right after a failed approval,
    // whose own error is already on screen).
    *getApprovalAttendanceCodes(id, silent = false) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getApprovalAttendanceCodes, { id }, false, null, false, silent);
            if (res?.ok) {
                return res.data || [];
            }
            return null;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    // approveStudent / rejectStudent return true on success, the BE payload ({ ok: false, message }) when
    // the BE refused the request (4xx business error), and false when the request itself failed —
    // network, 5xx or expired session, for which ApiStore resolves to nothing.
    *approveStudent(id, payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.approveStudent, { id, payload });
            if (res?.ok) {
                return true;
            }
            return res || false;
        } catch (error) {
            console.log(error);
            return false;
        }
    }

    *rejectStudent(id, payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.rejectStudent, { id, payload });
            if (res?.ok) {
                return true;
            }
            return res || false;
        } catch (error) {
            console.log(error);
            return false;
        }
    }

    *updateStudentEmployeeCode(id, employeeCode) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.updateStudentEmployeeCode, { id, payload: { employeeCode } });
            if(res?.ok) {
                yield this.getStudentDetail(id);
                return { ok: true };
            }
            return { ok: false, message: res?.message || 'Error' };
        } catch(error) {
            console.log(error);
            return { ok: false, message: error?.message || 'Error' };
        }
    }

    *getCompanyAttendanceRate(searchParams) {
        this.companyAttendanceRateSeq += 1;
        const seq = this.companyAttendanceRateSeq;
        this.loadingCompanyAttendanceRate = true;
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getCompanyAttendanceRate, searchParams, false, null, true);
            if (seq !== this.companyAttendanceRateSeq) return; // a newer request is in flight
            if (res?.ok) {
                this.companyAttendanceRateList = res?.data;
            }
        } catch (error) {
            console.log(error);
        } finally {
            if (seq === this.companyAttendanceRateSeq) this.loadingCompanyAttendanceRate = false;
        }
    }
    *getDetailCompanyAttendanceRate(id, searchParams) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getDetailCompanyAttendanceRate, { id: id, payload: searchParams });
            if (res?.ok) {
                this.studentListWithAttCompany = res?.data;
            }
        } catch (error) {
            console.log(error);
        }
    }

    *exportTestResultCsv(payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.exportTestResultCsv, payload);
        } catch (error) {
            console.log(error);
        }
    }

    *exportAttendanceStudentListCsv(payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.exportAttendanceStudentListCsv, payload);
        } catch (error) {
            console.log(error);
        }
    }

    *exportAttendanceCompleteRateCsv(payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.exportAttendanceCompleteRateCsv, payload);
        } catch (error) {
            console.log(error);
        }
    }

    *getAllChildrenGroupById(id, cb) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getAllChildrenGroupById, id);
            if (res?.ok) {
                this.childrenOfGroup = res?.data;
            }
            cb && cb();
        } catch (error) {
            console.log(error);
        }
    }

    *getCompanyDepartments() {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getCompanyDepartments, {});
            if (res?.ok) {
                this.childrenDepartments = res?.data || [];
            }
        } catch (error) {
            console.log(error);
        }
    }
    *resignation({ id, payload }) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.resignation, { id, payload });
            if(res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    clean() {
        super.clean();
        this.pagingStudentOfCompany = this.defaultPaging;
        this.gaFilterMode = 'group';
        this.childrenDepartments = [];
        this.expandTreeDepartmentIds = [];
        this.selectedTreeDepartmentIds = [];
        this.selectedDepartmentIds = [];
    }

    //#region test history ei
    *getTestHistoriesByStudent(searchParams) {
        try {
            const { size, page, sortDir, sortKey } = this.pagingTestHistoriesByStudent;
            const payload = { size, page, sortDir, sortKey, ...searchParams };
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getTestHistoriesByStudent, payload);
            if (res?.ok) {
                this.testHistoriesByStudent = res?.data?.elements || [];
                this.setAttrObservable('pagingTestHistoriesByStudent', res?.data?.paginate, true, false);
            }
        } catch (error) {
            console.log(error);
        }
    }
    *getCalculateAverageEi(payload) {
        try {
            const res = yield this.rootStore.apiStore.call(this.api, this.api.getCalculateAverageEi, payload);
            if (res?.ok) {
                return res?.data;
            }
            return false;
        } catch (error) {
            console.log(error);
            return false;
        }
    }
    //#endregion test history ei
}

export default GroupAdminStore;
